import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Boxes, Search, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { Toaster } from "sonner";

export default function AccountsStockSummary() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setBusy(true);
        api.get("/accounts/stock-summary")
            .then((r) => setData(r.data))
            .finally(() => setBusy(false));
    }, []);

    const rows = useMemo(() => {
        if (!data) return [];
        const needle = q.trim().toLowerCase();
        if (!needle) return data.rows;
        return data.rows.filter((r) => (r.product_name || "").toLowerCase().includes(needle));
    }, [data, q]);

    return (
        <div data-testid="accounts-stock-summary">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><Boxes className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Stock Summary</h1>
                    <p className="text-sm text-stone-600 mt-2">Closing stock per product, with valuation at retail price. Click a row to see size-wise breakdown.</p>
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product…" className="rounded-none border-stone-300 pl-9 w-72" data-testid="ss-search" />
                </div>
            </div>

            {data && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    <Card label="Products" value={data.rows.length} />
                    <Card label="Units in stock" value={data.grand_qty} />
                    <Card label="Inventory value (retail)" value={formatINR(data.grand_value)} cls="text-sky-700" big />
                </div>
            )}

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th></Th><Th>Product</Th><Th cls="text-right">Total qty</Th><Th cls="text-right">Sizes</Th><Th cls="text-right">Value (₹)</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {busy ? (
                            <tr><td colSpan={5} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : !rows.length ? (
                            <tr><td colSpan={5} className="p-8 text-center text-stone-500">No products in stock.</td></tr>
                        ) : rows.map((r, idx) => {
                            const open = expanded[r.product_id];
                            const lowSize = r.sizes.find((s) => s.stock <= 5);
                            return (
                                <React.Fragment key={r.product_id}>
                                    <tr className="border-b border-stone-100 hover:bg-sky-50/40 cursor-pointer" onClick={() => setExpanded({ ...expanded, [r.product_id]: !open })} data-testid={`ss-row-${idx}`}>
                                        <td className="p-2 w-6">{open ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}</td>
                                        <td className="p-2 font-medium text-slate-900">
                                            {r.product_name}
                                            {lowSize && <AlertCircle className="w-3 h-3 inline ml-2 text-amber-600" title="Has low-stock variants" />}
                                        </td>
                                        <td className="p-2 text-right font-mono">{r.total_qty}</td>
                                        <td className="p-2 text-right text-stone-500 text-xs">{r.sizes.length}</td>
                                        <td className="p-2 text-right font-mono text-sky-700">{formatINR(r.total_value)}</td>
                                    </tr>
                                    {open && (
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            <td colSpan={5} className="p-3">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="border-b border-stone-200 text-stone-500">
                                                            <th className="text-left py-1">Size</th><th className="text-left">SKU</th>
                                                            <th className="text-right">Stock</th><th className="text-right">Price</th><th className="text-right">Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {r.sizes.map((s) => (
                                                            <tr key={s.size_id} className="border-b border-stone-100">
                                                                <td className="py-1 font-mono">{s.size_label}</td>
                                                                <td className="font-mono text-stone-500">{s.sku || "—"}</td>
                                                                <td className={`text-right font-mono ${s.stock === 0 ? "text-red-700" : s.stock <= 5 ? "text-amber-700" : "text-stone-700"}`}>{s.stock}</td>
                                                                <td className="text-right font-mono">{formatINR(s.price)}</td>
                                                                <td className="text-right font-mono text-sky-700">{formatINR(s.value)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const Th = ({ children, cls = "" }) => <th className={`p-2 text-xs uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>;
const Card = ({ label, value, cls = "", big = false }) => (
    <div className="bg-white border border-sky-100 p-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`font-serif ${big ? "text-2xl" : "text-xl"} mt-1 ${cls}`}>{value}</div>
    </div>
);
